import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Label,
  Textarea,
} from '@artstorefronts/ui';
import { Loader2, CheckCircle, XCircle, AlertCircle, Bot, EyeOff, Check, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

import AdminLayout from '../../../components/AdminLayout';

const PAGE_SIZE = 25;

const ADMIN_VERIFICATION_REQUESTS_QUERY = gql`
  query AdminVerificationRequests($status: VerificationStatus, $limit: Int, $offset: Int) {
    adminVerificationRequests(status: $status, limit: $limit, offset: $offset) {
      items {
        id
        userId
        status
        denialReason
        reviewedAt
        createdAt
        user {
          asfId
          firstName
          lastName
          email
          displayName
          avatarUrl
          slug
        }
        reviewer {
          asfId
          firstName
          lastName
          displayName
        }
        scanSummary {
          totalImages
          cleanImages
          aiFlaggedImages
          nsfwFlaggedImages
          whitelistedImages
        }
      }
      totalCount
      hasMore
    }
  }
`;

const ADMIN_VERIFICATION_REQUEST_DETAIL = gql`
  query AdminVerificationRequestDetail($id: ID!) {
    adminVerificationRequest(id: $id) {
      id
      images {
        id
        thumbnailUrl
        optimizedUrl
        latestAiScan {
          id
          confidence
          isAiGenerated
          dismissedAt
          scannedAt
        }
        latestNsfwScan {
          id
          isFlagged
          rawResponse
          dismissedAt
          scannedAt
        }
      }
    }
  }
`;

const ADMIN_REVIEW_VERIFICATION = gql`
  mutation AdminReviewVerification($input: ReviewVerificationInput!) {
    adminReviewVerification(input: $input) {
      id
      status
      reviewedAt
    }
  }
`;

const DISMISS_SCAN = gql`
  mutation DismissScan($input: DismissScanInput!) {
    dismissScan(input: $input) {
      id
      resolution
    }
  }
`;

interface AiScan {
  id: string;
  confidence: number;
  isAiGenerated: boolean;
  dismissedAt: string | null;
  scannedAt: string;
}

interface NsfwScan {
  id: string;
  isFlagged: boolean;
  rawResponse: Record<string, unknown> | null;
  dismissedAt: string | null;
  scannedAt: string;
}

interface VerificationImage {
  id: string;
  thumbnailUrl: string | null;
  optimizedUrl: string | null;
  latestAiScan: AiScan | null;
  latestNsfwScan: NsfwScan | null;
}

interface ScanSummary {
  totalImages: number;
  cleanImages: number;
  aiFlaggedImages: number;
  nsfwFlaggedImages: number;
  whitelistedImages: number;
}

interface VerificationRequest {
  id: string;
  userId: string;
  status: string;
  denialReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
  user: {
    asfId: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
    displayName: string | null;
    avatarUrl: string | null;
    slug: string | null;
  };
  reviewer: {
    asfId: string;
    firstName: string | null;
    lastName: string | null;
    displayName: string | null;
  } | null;
  scanSummary: ScanSummary | null;
}

interface VerificationRequestDetail {
  id: string;
  images: VerificationImage[];
}

type StatusFilter = 'all' | 'under_review' | 'approved' | 'denied';

const FILTER_ORDER: StatusFilter[] = ['under_review', 'all', 'approved', 'denied'];

function filterTabLabel(filter: StatusFilter, t: TFunction<'admin'>, tc: TFunction<'common'>) {
  switch (filter) {
    case 'under_review':
      return t('verification.filter.needs-review');
    case 'all':
      return tc('all');
    case 'approved':
      return t('verification.filter.auto-approved');
    case 'denied':
      return t('verification.filter.auto-denied');
    default:
      return filter;
  }
}

function getStatusPillClasses(status: string) {
  switch (status) {
    case 'under_review':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'approved':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'denied':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'pending':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
}

function getStatusLabel(t: TFunction<'admin'>, status: string) {
  const fallback = status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  return t(`verification.request-status.${status}`, { defaultValue: fallback });
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getUserDisplayName(user: {
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string;
}) {
  return user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '';
}

function getFlaggedImages(images: VerificationImage[]) {
  const flagged: { image: VerificationImage; flagType: 'ai' | 'nsfw'; scan: AiScan | NsfwScan }[] = [];
  for (const image of images) {
    if (image.latestAiScan?.isAiGenerated && !image.latestAiScan.dismissedAt) {
      flagged.push({ image, flagType: 'ai', scan: image.latestAiScan });
    }
    if (image.latestNsfwScan?.isFlagged && !image.latestNsfwScan.dismissedAt) {
      flagged.push({ image, flagType: 'nsfw', scan: image.latestNsfwScan });
    }
  }
  return flagged;
}

export default function VerificationRequestsPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const [activeFilter, setActiveFilter] = useState<StatusFilter>('under_review');
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  // Snapshot at open: the warning must survive a list refetch or a null scanSummary.
  const [approveRequest, setApproveRequest] = useState<{ id: string; hasImages: boolean } | null>(null);
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [denialReason, setDenialReason] = useState('');
  const [revealedNsfwImages, setRevealedNsfwImages] = useState<Set<string>>(new Set());
  const [loadingMore, setLoadingMore] = useState(false);
  // The server offset advances a full page per fetch rather than tracking the
  // row count. When newer requests shift the window, a page can dedupe away to
  // nothing, and keying the offset off `requests.length` would then refetch the
  // same offset forever with Load more never advancing or disabling.
  const nextOffsetRef = useRef(PAGE_SIZE);

  const statusVariable = activeFilter === 'all' ? undefined : activeFilter;

  const { data, loading, fetchMore, refetch } = useQuery(ADMIN_VERIFICATION_REQUESTS_QUERY, {
    variables: { status: statusVariable, limit: PAGE_SIZE, offset: 0 },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Keyed off the expanded id rather than fired imperatively: Apollo then ties
  // `data` to the current variables, so a slow response for a row the admin has
  // already collapsed cannot land on top of the row they opened next.
  const {
    data: detailData,
    loading: detailLoading,
    refetch: refetchDetail,
  } = useQuery(ADMIN_VERIFICATION_REQUEST_DETAIL, {
    variables: { id: expandedRequestId },
    skip: !expandedRequestId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [adminReviewVerification, { loading: reviewing }] = useMutation(ADMIN_REVIEW_VERIFICATION);
  const [dismissScan, { loading: dismissing }] = useMutation(DISMISS_SCAN);

  const requests = (data?.adminVerificationRequests?.items || []) as VerificationRequest[];
  const totalCount = (data?.adminVerificationRequests?.totalCount ?? 0) as number;
  const hasMore = Boolean(data?.adminVerificationRequests?.hasMore);

  const detailRequest = detailData?.adminVerificationRequest as VerificationRequestDetail | null | undefined;
  const detailImages = detailRequest?.id === expandedRequestId ? detailRequest?.images : undefined;

  const refetchList = async () => {
    const limit = Math.max(requests.length, PAGE_SIZE);
    const result = await refetch({ status: statusVariable, limit, offset: 0 });
    nextOffsetRef.current = limit;
    return result;
  };

  const loadMoreRequests = useCallback(async () => {
    if (!hasMore || loading || loadingMore) return;

    const offset = nextOffsetRef.current;
    setLoadingMore(true);
    try {
      await fetchMore({
        variables: { status: statusVariable, limit: PAGE_SIZE, offset },
        updateQuery: (prev, { fetchMoreResult }) => {
          const incoming = fetchMoreResult?.adminVerificationRequests;
          if (!incoming) return prev;

          const existingIds = new Set(prev.adminVerificationRequests.items.map((request: any) => request.id));
          const newItems = incoming.items.filter((request: any) => !existingIds.has(request.id));

          // Adopt the incoming hasMore/totalCount even when the page came back
          // empty. A queue that shrinks mid-pagination (another moderator
          // clearing rows) would otherwise leave Load more enabled forever.
          return {
            adminVerificationRequests: {
              ...incoming,
              items: [...prev.adminVerificationRequests.items, ...newItems],
            },
          };
        },
      });
      nextOffsetRef.current = offset + PAGE_SIZE;
    } catch (error) {
      console.error('Error loading more verification requests:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchMore, hasMore, loading, loadingMore, statusVariable]);

  const selectFilter = (filter: StatusFilter) => {
    nextOffsetRef.current = PAGE_SIZE;
    setExpandedRequestId(null);
    setActiveFilter(filter);
  };

  const confirmApprove = async () => {
    if (!approveRequest) return;
    try {
      await adminReviewVerification({
        variables: { input: { requestId: approveRequest.id, decision: 'approved' } },
      });
      toast.success(t('verification.toast.approved'));
      setApproveRequest(null);
      refetchList();
    } catch (error: any) {
      console.error('Error approving verification:', error);
      toast.error(error.message || t('verification.toast.approve-error'));
    }
  };

  const handleDeny = (requestId: string) => {
    setSelectedRequestId(requestId);
    setDenialReason('');
    setDenyDialogOpen(true);
  };

  const confirmDeny = async () => {
    if (!selectedRequestId) return;
    try {
      await adminReviewVerification({
        variables: {
          input: { requestId: selectedRequestId, decision: 'denied', denialReason: denialReason.trim() || null },
        },
      });
      toast.success(t('verification.toast.denied'));
      setDenyDialogOpen(false);
      setSelectedRequestId(null);
      setDenialReason('');
      refetchList();
    } catch (error: any) {
      console.error('Error denying verification:', error);
      toast.error(error.message || t('verification.toast.deny-error'));
    }
  };

  const handleDismissScan = async (scanId: string) => {
    try {
      await dismissScan({
        variables: { input: { scanId } },
      });
      toast.success(t('verification.toast.scan-dismissed'));
      const pending: Promise<unknown>[] = [refetchList()];
      if (expandedRequestId) {
        pending.push(refetchDetail());
      }
      await Promise.all(pending);
    } catch (error: any) {
      console.error('Error dismissing scan:', error);
      toast.error(error.message || t('verification.toast.dismiss-scan-error'));
    }
  };

  const toggleNsfwReveal = (imageId: string) => {
    setRevealedNsfwImages((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) {
        next.delete(imageId);
      } else {
        next.add(imageId);
      }
      return next;
    });
  };

  const toggleExpanded = (requestId: string) => {
    setExpandedRequestId(expandedRequestId === requestId ? null : requestId);
  };

  if (loading && !data) {
    return (
      <AdminLayout title={t('verification.page-title')} description={t('verification.page-description')}>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={t('verification.page-title')} description={t('verification.page-description')}>
      {/* Filter Tabs */}
      <div className="mb-6">
        <div className="flex gap-2 border-b border-gray-200">
          {FILTER_ORDER.map((filter) => (
            <button
              key={filter}
              onClick={() => selectFilter(filter)}
              className={`px-4 py-2 text-sm border-b-2 -mb-[1px] transition-colors ${
                activeFilter === filter
                  ? 'border-black text-black font-semibold'
                  : 'border-transparent text-gray-500 hover:text-gray-900 font-medium'
              }`}
            >
              {filterTabLabel(filter, t, tc)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-[1fr_120px_120px_80px_180px] py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wide">
            <div>{t('verification.table.artist')}</div>
            <div>{t('verification.table.submitted')}</div>
            <div>{t('verification.table.status')}</div>
            <div>{t('verification.table.images')}</div>
            <div>{t('verification.table.scan-results')}</div>
          </div>
        </div>

        <div>
          {requests.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>{t('verification.empty-state')}</p>
            </div>
          ) : (
            requests.map((request) => {
              const isExpanded = expandedRequestId === request.id;
              const summary = request.scanSummary;
              const flaggedImages = isExpanded && detailImages ? getFlaggedImages(detailImages) : [];
              const flaggedCount = summary ? summary.aiFlaggedImages + summary.nsfwFlaggedImages : 0;
              const hasImages = (summary?.totalImages ?? 0) > 0;
              const allClean = summary ? hasImages && summary.cleanImages === summary.totalImages : true;

              return (
                <div key={request.id} className={isExpanded ? 'bg-gray-50/50' : ''}>
                  {/* Table Row */}
                  <div
                    className="grid grid-cols-[1fr_120px_120px_80px_180px] items-center px-4 py-3 text-sm hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                    onClick={() => toggleExpanded(request.id)}
                  >
                    {/* Artist */}
                    <div className="flex items-center gap-3 min-w-0">
                      <ChevronRight
                        className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                      />
                      <a
                        href={request.user.slug ? `${__TOWN_SQUARE_URL__}/${request.user.slug}` : undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className={request.user.slug ? 'shrink-0 hover:opacity-80 transition-opacity' : 'shrink-0'}
                      >
                        {request.user.avatarUrl ? (
                          <img
                            src={request.user.avatarUrl}
                            alt={getUserDisplayName(request.user)}
                            className="w-7 h-7 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-xs font-medium">
                            {getUserDisplayName(request.user).charAt(0).toUpperCase()}
                          </div>
                        )}
                      </a>
                      <div className="min-w-0">
                        <a
                          href={request.user.slug ? `${__TOWN_SQUARE_URL__}/${request.user.slug}` : undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className={
                            request.user.slug
                              ? 'font-medium text-gray-900 hover:underline truncate block'
                              : 'font-medium text-gray-900 truncate block'
                          }
                        >
                          {getUserDisplayName(request.user)}
                        </a>
                        <div className="text-xs text-gray-500 truncate">{request.user.email}</div>
                      </div>
                    </div>

                    {/* Submitted */}
                    <div className="text-gray-600 text-xs">{formatDate(request.createdAt)}</div>

                    {/* Status */}
                    <div>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${getStatusPillClasses(request.status)}`}
                      >
                        {getStatusLabel(t, request.status)}
                      </span>
                    </div>

                    {/* Images */}
                    <div className="text-gray-600 text-sm">{summary?.totalImages ?? 0}</div>

                    {/* Scan Results */}
                    <div className="flex items-center gap-2">
                      {summary && !hasImages && (
                        <span className="text-xs text-gray-500">{t('verification.no-reviewable-images')}</span>
                      )}
                      {summary && hasImages && (
                        <>
                          <span className={`text-xs font-medium ${allClean ? 'text-green-700' : 'text-amber-700'}`}>
                            {t('verification.clean-ratio', {
                              clean: summary.cleanImages,
                              total: summary.totalImages,
                            })}
                          </span>
                          {summary.aiFlaggedImages > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-xs text-red-700 bg-red-50 px-1.5 py-0.5 rounded">
                              <Bot className="w-3 h-3" />
                              {summary.aiFlaggedImages}
                            </span>
                          )}
                          {summary.nsfwFlaggedImages > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-xs text-orange-700 bg-orange-50 px-1.5 py-0.5 rounded">
                              <EyeOff className="w-3 h-3" />
                              {summary.nsfwFlaggedImages}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Expanded Detail */}
                  {isExpanded && (
                    <div className="border-b border-gray-200 px-4 py-5">
                      {/* Denial Reason */}
                      {request.denialReason && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                            <div>
                              <div className="text-sm font-medium text-red-900 mb-1">
                                {t('verification.denial-reason')}
                              </div>
                              <div className="text-sm text-red-800">{request.denialReason}</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Reviewed Info */}
                      {request.reviewedAt && (
                        <div className="text-xs text-gray-500 mb-4">
                          {t('verification.reviewed-on', { date: formatDate(request.reviewedAt) })}
                          {request.reviewer
                            ? t('verification.reviewed-by', { name: getUserDisplayName(request.reviewer) })
                            : ''}
                        </div>
                      )}

                      {/* Flagged Images Grid */}
                      {!detailImages && detailLoading && (
                        <div className="flex justify-center py-6">
                          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                        </div>
                      )}

                      {flaggedImages.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">
                            {t('verification.flagged-images-heading', { count: flaggedImages.length })}
                          </h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                            {flaggedImages.map(({ image, flagType, scan }) => {
                              const isNsfw = flagType === 'nsfw';
                              const isRevealed = revealedNsfwImages.has(image.id + '-' + flagType);
                              const thumbUrl = image.thumbnailUrl || image.optimizedUrl;

                              return (
                                <div
                                  key={`${image.id}-${flagType}`}
                                  className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                                >
                                  {/* Thumbnail */}
                                  <div
                                    className={`relative aspect-square bg-gray-100 ${isNsfw && !isRevealed ? 'cursor-pointer' : ''}`}
                                    onClick={
                                      isNsfw && !isRevealed
                                        ? () => toggleNsfwReveal(image.id + '-' + flagType)
                                        : undefined
                                    }
                                  >
                                    {thumbUrl ? (
                                      <img
                                        src={thumbUrl}
                                        alt=""
                                        className={`w-full h-full object-cover ${isNsfw && !isRevealed ? 'blur-[20px]' : ''}`}
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                                        {t('verification.no-image')}
                                      </div>
                                    )}
                                    {isNsfw && !isRevealed && (
                                      <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="bg-black/60 text-white text-xs px-2 py-1 rounded">
                                          {t('verification.click-to-reveal')}
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  {/* Badge + Action */}
                                  <div className="p-2">
                                    {flagType === 'ai' ? (
                                      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-1.5 py-0.5 rounded mb-2">
                                        <Bot className="w-3 h-3" />
                                        {t('verification.ai-confidence', {
                                          percent: Math.round((scan as AiScan).confidence * 100),
                                        })}
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-700 bg-orange-50 px-1.5 py-0.5 rounded mb-2">
                                        <EyeOff className="w-3 h-3" />
                                        {t('verification.nsfw')}
                                      </span>
                                    )}

                                    <button
                                      onClick={() => handleDismissScan(scan.id)}
                                      disabled={dismissing}
                                      className="w-full flex items-center justify-center gap-1 text-xs font-medium text-gray-500 hover:bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1 transition-colors disabled:opacity-50"
                                    >
                                      <Check className="w-3.5 h-3.5" />
                                      {t('verification.allow')}
                                    </button>
                                    <p className="text-[10px] text-gray-400 mt-1 text-center">
                                      {t('verification.whitelist-hint')}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Action Bar */}
                      {(request.status === 'under_review' || request.status === 'pending') && (
                        <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3">
                          <div className="text-sm text-gray-600">
                            {summary && !hasImages && t('verification.action-bar.no-images')}
                            {summary && hasImages && (
                              <>
                                {t('verification.action-bar.clean-summary', {
                                  clean: summary.cleanImages,
                                  total: summary.totalImages,
                                })}
                                {flaggedCount > 0
                                  ? t('verification.action-bar.flagged-suffix', { count: flaggedCount })
                                  : ''}
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeny(request.id)}
                              disabled={reviewing}
                              className="border-gray-200 text-gray-500 hover:bg-gray-50"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              {t('verification.deny')}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => setApproveRequest({ id: request.id, hasImages })}
                              disabled={reviewing}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              {tc('approve')}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Pagination */}
      {requests.length > 0 && (
        <div className="mt-4 flex flex-col items-center gap-3">
          <div className="text-xs text-gray-500">
            {t('verification.showing-count', { shown: requests.length, total: totalCount })}
          </div>
          {hasMore && (
            <Button
              variant="outline"
              onClick={loadMoreRequests}
              disabled={loadingMore}
              className="border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              {loadingMore ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{tc('loading-more')}</span>
                </div>
              ) : (
                <span>{tc('load-more')}</span>
              )}
            </Button>
          )}
        </div>
      )}

      {/* Approve Confirmation Dialog */}
      <Dialog open={!!approveRequest} onOpenChange={() => setApproveRequest(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('verification.approve-dialog.title')}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <p className="text-sm text-gray-600">{t('verification.approve-dialog.body')}</p>
            {approveRequest && !approveRequest.hasImages && (
              <p className="text-sm text-amber-700">{t('verification.approve-dialog.no-images-warning')}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setApproveRequest(null)}>
              {tc('cancel')}
            </Button>
            <Button
              type="button"
              onClick={confirmApprove}
              disabled={reviewing}
              className="bg-green-600 hover:bg-green-700"
            >
              {reviewing ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{t('verification.approve-dialog.approving')}</span>
                </div>
              ) : (
                <span>{tc('approve')}</span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deny Dialog */}
      <Dialog open={denyDialogOpen} onOpenChange={() => setDenyDialogOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('verification.deny-dialog.title')}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-gray-600">{t('verification.deny-dialog.body')}</p>
            <div>
              <Label htmlFor="denialReason">{t('verification.deny-dialog.reason-label')}</Label>
              <Textarea
                id="denialReason"
                value={denialReason}
                onChange={(e) => setDenialReason(e.target.value)}
                placeholder={t('verification.deny-dialog.reason-placeholder')}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDenyDialogOpen(false)}>
              {tc('cancel')}
            </Button>
            <Button
              type="button"
              onClick={confirmDeny}
              disabled={reviewing}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {reviewing ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{t('verification.deny-dialog.denying')}</span>
                </div>
              ) : (
                <span>{t('verification.deny-dialog.confirm')}</span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
